import { z } from 'zod';

// Shared between backend (controller DTO + use case input) and frontend
// (admin form + API client). Events module (§6.7) — S1 core + templates.
// Enum values MUST mirror apps/backend/prisma/schema.prisma exactly (S1a) and
// the domain entity's local types in event.entity.ts / event-template.entity.ts.

export const EventStatusSchema = z.enum(['draft', 'scheduled', 'active', 'closed']);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const EventChargeModeSchema = z.enum(['per_attendee', 'forfait', 'combination', 'free']);
export type EventChargeMode = z.infer<typeof EventChargeModeSchema>;

export const EventPhotoPolicySchema = z.enum(['required_4', 'deferred', 'optional']);
export type EventPhotoPolicy = z.infer<typeof EventPhotoPolicySchema>;

export const EventSettlementStatusSchema = z.enum([
  'not_applicable',
  'pending',
  'settled',
  'failed',
]);
export type EventSettlementStatus = z.infer<typeof EventSettlementStatusSchema>;

export const EventTemplateCategorySchema = z.enum(['catering', 'corporate', 'wedding']);
export type EventTemplateCategory = z.infer<typeof EventTemplateCategorySchema>;

// ── Event create/update/response (R1.1-R1.6) ────────────────────────────────

export const CreateEventRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  locationId: z.string().uuid('locationId must be a valid uuid'),
  // Calendar date only (YYYY-MM-DD), stored as @db.Date.
  scheduledDate: z.string().date('scheduledDate must be YYYY-MM-DD'),
  // 24h HH:mm, optional.
  scheduledTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'scheduledTime must be HH:mm (24h)')
    .nullable()
    .optional(),
  expectedCapacity: z.number().int().min(1).max(999999).nullable().optional(),
  chargeMode: EventChargeModeSchema,
  photoPolicy: EventPhotoPolicySchema.optional(),
  // R1.7/R1.8 — optional template reference; must belong to actor's org (404 otherwise).
  templateId: z.string().uuid().nullable().optional(),
});
export type CreateEventRequest = z.infer<typeof CreateEventRequestSchema>;

// Update is intentionally NOT `CreateEventRequestSchema.partial()`:
// templateId is creation-time-only in S1b scope (no spec scenario requires
// patching it post-create). photoPolicy IS patchable post-create (event-
// photo-capture change) but gated by the same R1.4 field-editability matrix
// (editable while draft/scheduled, frozen once active/closed) via the
// `EditableEventField` union — see update-event.use-case.ts.
export const UpdateEventRequestSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    locationId: z.string().uuid('locationId must be a valid uuid'),
    scheduledDate: z.string().date('scheduledDate must be YYYY-MM-DD'),
    scheduledTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'scheduledTime must be HH:mm (24h)')
      .nullable(),
    expectedCapacity: z.number().int().min(1).max(999999).nullable(),
    chargeMode: EventChargeModeSchema,
    photoPolicy: EventPhotoPolicySchema,
  })
  .partial();
export type UpdateEventRequest = z.infer<typeof UpdateEventRequestSchema>;

export const EventResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  locationId: z.string().uuid(),
  templateId: z.string().uuid().nullable(),
  name: z.string(),
  scheduledDate: z.string(),
  scheduledTime: z.string().nullable(),
  expectedCapacity: z.number().int().nullable(),
  status: EventStatusSchema,
  chargeMode: EventChargeModeSchema,
  photoPolicy: EventPhotoPolicySchema,
  settlementStatus: EventSettlementStatusSchema,
  settlementPaymentIntentId: z.string().nullable(),
  settledTotalCents: z.number().int().nullable(),
  currency: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  closedAt: z.string().nullable(),
});
export type EventResponse = z.infer<typeof EventResponseSchema>;

export const ListEventsResponseSchema = z.object({ events: z.array(EventResponseSchema) });
export type ListEventsResponse = z.infer<typeof ListEventsResponseSchema>;

// ── Event templates (R1.7/R1.8) ──────────────────────────────────────────────

export const CreateEventTemplateRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  category: EventTemplateCategorySchema,
  defaultConfig: z.record(z.string(), z.unknown()).optional(),
});
export type CreateEventTemplateRequest = z.infer<typeof CreateEventTemplateRequestSchema>;

export const EventTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  category: EventTemplateCategorySchema,
  defaultConfig: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EventTemplateResponse = z.infer<typeof EventTemplateResponseSchema>;

export const ListEventTemplatesResponseSchema = z.object({
  templates: z.array(EventTemplateResponseSchema),
});
export type ListEventTemplatesResponse = z.infer<typeof ListEventTemplatesResponseSchema>;

// ── S6 — close + report (R6.1-R6.6) ─────────────────────────────────────────

// R6.4 — force-close override for per_attendee events with unsettled payments.
// org_admin-only enforcement happens at the use-case layer (actor role, not schema).
export const CloseEventRequestSchema = z.object({
  forceClose: z.boolean().optional(),
});
export type CloseEventRequest = z.infer<typeof CloseEventRequestSchema>;

export const EventCloseReportResponseSchema = z.object({
  revenueTotalCents: z.number().int(),
  volume: z.number().int(),
  expectedCapacity: z.number().int().nullable(),
  capacityVarianceCount: z.number().int().nullable(),
  averageRetrievalTimeSeconds: z.number().int().nullable(),
  hasOutstandingPayments: z.boolean(),
  unsettledTicketIds: z.array(z.string().uuid()),
  generatedAt: z.string(),
});
export type EventCloseReportResponse = z.infer<typeof EventCloseReportResponseSchema>;

// ── Operator-scoped GET /events/active (S4-UI prerequisite) ────────────────
// Deliberately a MINIMAL subset of EventResponseSchema — no chargeMode,
// settlement, or closeReport internals exposed to the operator role. Mirrors
// the getMyLocations() "my assigned resources" precedent
// (location-assignments module) rather than relaxing requireEventManagerActor.

export const EventSummaryResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  scheduledDate: z.string(),
  scheduledTime: z.string().nullable(),
  locationId: z.string().uuid(),
  status: EventStatusSchema,
  // event-photo-capture: Flash reads this to gate the angle-capture UI.
  photoPolicy: EventPhotoPolicySchema,
});
export type EventSummaryResponse = z.infer<typeof EventSummaryResponseSchema>;

export const ListActiveEventsResponseSchema = z.object({
  events: z.array(EventSummaryResponseSchema),
});
export type ListActiveEventsResponse = z.infer<typeof ListActiveEventsResponseSchema>;
